/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

/**
 * Header search-suggestions hook.
 *
 * Sprint 1 refactor: this is now a thin facade over whichever
 * {@link SearchSuggestionsAdapter} is registered via the
 * {@link SearchSuggestionsProvider}. The dual-branch
 * `constructorEnabled ? useAutocomplete : useSearchSuggestions(SCAPI)`
 * branch in `header/search.tsx` collapses into this single hook —
 * swapping data sources is now a config-only change
 * (`PUBLIC__app__searchSuggestions__adapter=mock|constructor|einstein`).
 *
 * Behaviour preserved from the legacy hook:
 *   - 100ms-default debounce (caller can override).
 *   - Aborts the prior in-flight request when the query changes so a
 *     stale response can never paint over a newer one.
 *   - Refetch helper for the manual-trigger path used by the header
 *     debounce (kept on the return type so the existing call sites
 *     and tests continue to compile).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchSuggestionsAdapter } from '@/providers/search-suggestions';
import type { SearchSuggestionsResult } from '@/lib/adapters/search-suggestions-types';

const DEFAULT_DEBOUNCE_MS = 100;
const DEFAULT_NUM_RESULTS = 8;

const EMPTY_RESULT: SearchSuggestionsResult = {
    phraseSuggestions: [],
    productSuggestions: [],
    categorySuggestions: [],
};

export interface UseSearchSuggestionsOptions {
    q: string;
    /** Per-section result cap forwarded to the adapter. */
    numResults?: number;
    /** Debounce applied to query changes. Defaults to 100ms. */
    debounceMs?: number;
    /** Disable the hook entirely (e.g. flag-off branch). */
    enabled?: boolean;
}

export interface UseSearchSuggestionsResult {
    /**
     * Latest result for the current `q`. `null` when nothing has been
     * fetched yet or when the upstream returned an empty response.
     */
    data: SearchSuggestionsResult | null;
    isLoading: boolean;
    /**
     * Re-fetch immediately for the current `q`. Used by the header
     * debounced "user kept typing past the threshold" path. Resolves
     * silently on abort/empty/disabled — never throws because the
     * header's `shouldOpenPopover()` treats a thrown promise as a
     * "stop showing the popover" signal.
     */
    refetch: () => Promise<void>;
}

export function useSearchSuggestions(options: UseSearchSuggestionsOptions): UseSearchSuggestionsResult {
    const { q, numResults = DEFAULT_NUM_RESULTS, debounceMs = DEFAULT_DEBOUNCE_MS, enabled = true } = options;
    const adapter = useSearchSuggestionsAdapter();

    const [data, setData] = useState<SearchSuggestionsResult | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const abortRef = useRef<AbortController | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The adapter ref lets `refetch` see the latest adapter without
    // forcing a useCallback dependency on the adapter object identity
    // (which can change when the provider re-renders).
    const adapterRef = useRef(adapter);
    adapterRef.current = adapter;

    const runQuery = useCallback(
        async (query: string) => {
            const trimmed = query.trim();
            const liveAdapter = adapterRef.current;
            if (!enabled || !trimmed || !liveAdapter) {
                setData(null);
                setIsLoading(false);
                return;
            }

            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setIsLoading(true);

            try {
                const result = await liveAdapter.getSuggestions({
                    q: trimmed,
                    numResults,
                    signal: controller.signal,
                });
                if (controller.signal.aborted) return;
                const hasAny =
                    result.phraseSuggestions.length > 0 ||
                    result.productSuggestions.length > 0 ||
                    result.categorySuggestions.length > 0 ||
                    (result.popularSearchSuggestions?.length ?? 0) > 0;
                setData(hasAny ? result : { ...EMPTY_RESULT, searchPhrase: trimmed });
            } catch (error) {
                // AbortError is expected on rapid keystrokes — keep the
                // last-good `data` in place rather than blanking the UI.
                if ((error as { name?: string } | null)?.name === 'AbortError') return;
                setData(null);
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            }
        },
        [enabled, numResults]
    );

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!enabled || !q.trim() || !adapter) {
            setData(null);
            setIsLoading(false);
            return;
        }
        debounceRef.current = setTimeout(() => {
            void runQuery(q);
        }, debounceMs);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [q, debounceMs, enabled, runQuery, adapter]);

    useEffect(
        () => () => {
            abortRef.current?.abort();
            if (debounceRef.current) clearTimeout(debounceRef.current);
        },
        []
    );

    const refetch = useCallback(async (): Promise<void> => {
        await runQuery(q);
    }, [runQuery, q]);

    return { data, isLoading, refetch };
}
